/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { Crown, Star, ExternalLink, Trash2, RefreshCw, BookOpen, Filter } from "lucide-react";

const scoreColor = (s) => (s == null ? "text-slate-400" : s >= 85 ? "text-emerald-600" : s >= 70 ? "text-blue-600" : s >= 55 ? "text-amber-600" : "text-red-600");

export default function GoldenScripts() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [intent, setIntent] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/telephony/golden-scripts", { params: intent ? { intent } : {} }); setData(d); }
    catch { setData(null); }
    setLoading(false);
  }, [api, intent]);
  useEffect(() => { load(); }, [load]);

  const unpin = async (id) => {
    try { await api.delete(`/telephony/golden-scripts/${id}`); toast.success(zh ? "已移除" : "Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "移除失败" : "Failed")); }
  };

  const intents = Array.from(new Set((data?.scripts || []).map((s) => s.intentTag).filter(Boolean)));
  const scripts = data?.scripts || [];

  return (
    <div className="space-y-5" data-testid="golden-scripts-page">
      <div className="flex items-center gap-3 flex-wrap">
        <BookOpen className="h-6 w-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{zh ? "金牌话术库" : "Golden Scripts Library"}</h1>
          <p className="text-sm text-slate-400 mt-0.5">{zh ? "经理精选的高分话术，新销售随时学习复用" : "Manager-curated top scripts for new sales to learn anytime"}</p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {intents.length > 0 && (
            <div className="inline-flex items-center gap-1.5 text-sm">
              <Filter className="h-4 w-4 text-slate-400" />
              <select data-testid="golden-intent-filter" value={intent} onChange={(e) => setIntent(e.target.value)}
                className="border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-200">
                <option value="">{zh ? "全部意向" : "All intents"}</option>
                {intents.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          )}
          <button data-testid="golden-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg px-3 py-1.5">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {!data?.canManage && (
        <p className="text-xs text-slate-400" data-testid="golden-browse-hint">{zh ? "浏览模式：由销售经理在「话务分析」页收藏话术后在此展示。" : "Browse mode — managers pin scripts from Call Analytics."}</p>
      )}

      {scripts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center" data-testid="golden-empty">
          <Crown className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">{zh ? "话术库还是空的" : "Library is empty"}</p>
          <p className="text-sm text-slate-400 mt-1">{data?.canManage ? (zh ? "去「话务分析」页，把周榜金牌话术或高分通话点「收藏」加入这里。" : "Pin golden/top scripts from Call Analytics.") : (zh ? "经理收藏话术后会显示在这里。" : "Pinned scripts will appear here.")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {scripts.map((s) => (
            <div key={s.id} data-testid={`golden-card-${s.id}`} className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white p-4 shadow-sm">
              <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4 text-amber-500" />
                <span className="text-xs font-semibold text-slate-700">{s.agentName || (zh ? "销售" : "Agent")}</span>
                {s.intentTag && <span className="text-[10px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800">{s.intentTag}</span>}
                {s.score != null && <span className={`ml-auto inline-flex items-center gap-0.5 text-sm font-bold ${scoreColor(s.score)}`}><Star className="h-3.5 w-3.5 fill-current" />{s.score}</span>}
              </div>
              {s.highlight && <p className="text-sm text-slate-800 border-l-2 border-amber-400 pl-2 mb-1.5">“{s.highlight}”</p>}
              {s.summary && <p className="text-xs text-slate-500 mb-1.5">{s.summary}</p>}
              {s.note && <p className="text-[11px] text-amber-700 bg-amber-100/60 rounded px-2 py-1 mb-1.5">{zh ? "点评：" : "Note: "}{s.note}</p>}
              <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mt-1">
                <span className="text-[11px] text-slate-400">{zh ? "收藏人 " : "By "}{s.pinnedBy || "—"}</span>
                {s.leadId && <button onClick={() => navigate(`/crm/leads/${s.leadId}`)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-amber-700 hover:underline inline-flex items-center gap-0.5 text-xs">{zh ? "客户" : "Lead"}<ExternalLink className="h-3 w-3" /></button>}
                {data?.canManage && (
                  <button data-testid={`golden-unpin-${s.id}`} onClick={() => unpin(s.id)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-slate-400 hover:text-red-600 inline-flex items-center gap-1 text-xs">
                    <Trash2 className="h-3.5 w-3.5" />{zh ? "移除" : "Remove"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
